import { useRef, useCallback, useState } from 'react';

type UsePollingOptions<T> = {
  onErrorCallback: (err: Error) => void;
  shouldStopFn?: (result: T) => boolean;
  intervalMs?: number;
  isImmediate?: boolean;
  maxRuns?: number;
};

interface usePollingProps<T> {
  pollFn: () => Promise<T>;
  options: UsePollingOptions<T>;
}

export const usePolling = <T>({ pollFn, options }: usePollingProps<T>) => {
  const [isPolling, setIsPolling] = useState(false);
  const {
    onErrorCallback,
    shouldStopFn,
    intervalMs = 1000,
    isImmediate = true,
    maxRuns = 20,
  } = options;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const togglePolling = () => setIsPolling((prevState) => !prevState);
  const runCountRef = useRef(0);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    runCountRef.current = 0;
    togglePolling();
  }, []);

  const start = useCallback(() => {
    if (isPolling) return;

    const run = async () => {
      try {
        runCountRef.current += 1;

        const result = await pollFn();

        if (shouldStopFn?.(result)) {
          stop();
        }

        if (runCountRef.current >= maxRuns) {
          throw new Error('POLL_INTERVAL_TIMEOUT');
        }
      } catch (err) {
        stop();
        onErrorCallback(err as Error);
      }
    };

    if (isImmediate) run();

    intervalRef.current = setInterval(run, intervalMs);
    togglePolling();
  }, [
    isPolling,
    isImmediate,
    intervalMs,
    pollFn,
    shouldStopFn,
    stop,
    maxRuns,
    onErrorCallback,
  ]);

  return { stop, start, isPolling };
};
